import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useDocument } from './document';

interface PageProps {
  pageNumber: number;
  scale?: number;
  rotate?: number;
  width?: number | null;
  height?: number | null;
  exclude?: boolean;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onMouseUp?: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  canvasRef?: React.RefObject<HTMLCanvasElement>;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export interface PageRef {
  canvas: HTMLCanvasElement | null;
  currentRotation: number;
  pageNumber: number;
}

export const Page = forwardRef<PageRef, PageProps>(
  (
    {
      pageNumber,
      scale = 1,
      rotate = 0,
      width = null,
      height = null,
      exclude = false,
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onDoubleClick,
      canvasRef,
      className,
      style,
      children,
    },
    ref,
  ) => {
    const { pdf } = useDocument();
    const internalCanvasRef = useRef<HTMLCanvasElement>(null);
    const renderTaskRef = useRef<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const isMountedRef = useRef(true);

    // Use external ref if provided, otherwise use internal ref
    const canvasElement = canvasRef || internalCanvasRef;

    useImperativeHandle(
      ref,
      () => ({
        canvas: canvasElement.current,
        currentRotation: rotate,
        pageNumber: pageNumber,
      }),
      [canvasElement, rotate, pageNumber],
    );

    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      const renderPage = async () => {
        if (!pdf || !canvasElement.current || !isMountedRef.current) return;

        try {
          setIsLoading(true);

          // Cancel previous render task
          if (renderTaskRef.current) {
            try {
              renderTaskRef.current.cancel();
            } catch (e) {
              // Ignore cancellation errors
            }
            renderTaskRef.current = null;
          }

          const page = await pdf.getPage(pageNumber);

          if (!isMountedRef.current) return;

          const canvas = canvasElement.current;
          const context = canvas.getContext('2d');

          if (!context) return;

          // Calculate viewport with rotation
          let viewport = page.getViewport({ scale, rotation: rotate });

          // Apply custom width/height if specified
          if (width !== null || height !== null) {
            const naturalViewport = page.getViewport({ scale: 1, rotation: rotate });
            let newScale = scale;

            if (width !== null) {
              newScale = width / naturalViewport.width;
            }
            if (height !== null) {
              newScale = height / naturalViewport.height;
            }

            viewport = page.getViewport({ scale: newScale, rotation: rotate });
          }

          // Set canvas dimensions
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;

          // Clear canvas
          context.clearRect(0, 0, canvas.width, canvas.height);

          // Render page
          const renderContext = {
            canvasContext: context,
            viewport: viewport,
          };

          const renderTask = page.render(renderContext);
          renderTaskRef.current = renderTask;

          await renderTask.promise;

          if (isMountedRef.current) {
            setIsLoading(false);
          }
        } catch (error) {
          if (isMountedRef.current && (error as any)?.name !== 'RenderingCancelledException') {
            console.error('Error rendering page:', error);
            setIsLoading(false);
          }
        }
      };

      renderPage();

      return () => {
        if (renderTaskRef.current) {
          try {
            renderTaskRef.current.cancel();
          } catch (e) {
            // Ignore cancellation errors
          }
          renderTaskRef.current = null;
        }
      };
    }, [pdf, pageNumber, scale, rotate, width, height, canvasElement]);

    return (
      <>
        <canvas
          className={className}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onDoubleClick={onDoubleClick}

          ref={canvasElement}
          style={{
            display: 'block',
            opacity: exclude ? 0.2 : isLoading ? 0.5 : 1,
            transition: 'opacity 0.2s ease-in-out',
            boxShadow: '0px 1px 8px 2px rgba(0, 0, 0, 0.2)',
            ...style,
          }}
        />
        {children}
      </>
    );
  },
);

Page.displayName = 'Page';
